// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { faXmark } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IonButton, IonNav } from "@ionic/react"
import axios, { AxiosInstance } from "axios"
import { useEffect, useState } from "react"
import { useLocales } from "react-localized"
import ReactMarkdown from 'react-markdown'


const http: AxiosInstance = axios.create({
  baseURL: "/assets/kb",
  validateStatus: (status) => [200, 404].includes(status)
})


interface KnowledgeBasePanelProps {
  article: string | null
  articleContent: string | null
  isOpen: boolean
  setArticle: (article: string | null) => void
  setContent: (article: string | null) => void
}


const KnowledgeBasePanel: React.FC<KnowledgeBasePanelProps> = ({article, articleContent, isOpen, setArticle, setContent}) => {
  // TODO: validate that the URL is accepted.
  const {gettext, locale} = useLocales()
  const classNames: string[] = ["kb"]
  const currentUrl = new URL(window.location.href)
  const constructPath: (path: string) => string = (path) => { return `/${locale}/${article}.md`}
  const [documentText, setDocumentText] = useState<string | null>(articleContent)
  const defaultLanguage: string = "en"

  const retrieveDocument: (path: string) => Promise<string> = async (path) => {
    let doc: string = `The document ${path} does not exist.`
    let response = await http.get(constructPath(path))
    if (response.status === 404 && locale !== defaultLanguage) {
      response = await http.get(`/${defaultLanguage}/${path}.md`)
    }
    if (response.status === 200) {
      doc = response.data
    }
    return doc
  }

  useEffect(() => {
    if (article !== null) {
      retrieveDocument(constructPath(article)).then((doc) => {
        setContent(doc)
      })
    }
  }, [article])

  useEffect(() => { if (article === null) { setDocumentText(null)} }, [article])

  if (articleContent !== null || !!currentUrl.searchParams.get("kb")) {
    classNames.push("expand")
  }

  return (
    <div className={classNames.join(" ")}>
      <div className="content">
        <div className="header">
          <IonButton
            fill="clear"
            onClick={() => setArticle(null)}
          >
            <span>
              <FontAwesomeIcon icon={faXmark} size="2x"/>
            </span>
          </IonButton>
          <h2>{gettext("Knowledge base")}</h2>
        </div>
        <article style={{padding: "var(--section-padding)"}}>
          <ReactMarkdown
            components={{
              a: (props) => {
                return (
                  <a
                    onClick={() => {
                      setArticle(props.href || "")
                    }}
                    style={{cursor: "pointer", textDecoration: "underline"}}
                  >
                    {props.children[0]}
                  </a>
                )
              }
            }}
          >
            {articleContent || ""}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  )
}


export { KnowledgeBasePanel }